import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";

import { useUser } from "../../../user/useUser/useUser";
import { useAdonisChat } from "../../AdonisChatProvider/AdonisChatProvider";
import ChatRecommendations from "../../ChatRecommendations/ChatRecommendations";
import { defaultAdonisStarterQuestions } from "../../ChatRecommendations/data";

import adonisAdvisorImage from "./adonis.png";
import ariaAdvisorImage from "./aria.png";

interface ChatEmptyStateProps {
  isLoadingRecommendations: boolean;
  keyboardIsOpen: boolean;
  newChatLabel: string;
  starterQuestions?: { id: string; text: string }[];
  isWarmupComplete: boolean;
  isWarmingUp: boolean;
  warmupError: boolean;
  isStreaming: boolean;
  onSendRecommendation: (text: string) => void;
  headerTemplate: string;
  hasPendingMessage?: boolean;
}

type AdonisChatFixedHeaderProps = {
  readonly personalizedHeader: string;
};

function AdonisChatFixedHeader({
  personalizedHeader,
}: AdonisChatFixedHeaderProps) {
  return (
    <motion.div
      className="flex items-start gap-3 px-7 py-4"
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      <p className="w-full text-center font-serif text-[25px] font-medium leading-[30px] text-black">
        {personalizedHeader}
      </p>
    </motion.div>
  );
}

const AdvisorImage = ({ src }: { src: string }) => {
  const [imageLoaded, setImageLoaded] = useState(false);

  return (
    <div className="pointer-events-none flex min-h-0 flex-1 items-end justify-center overflow-hidden">
      <motion.div
        className="flex h-full w-full items-end justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: imageLoaded ? 1 : 0 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
      >
        <img
          src={src}
          alt="Advisor"
          className="max-h-full w-auto object-contain object-bottom"
          onLoad={() => setImageLoaded(true)}
        />
      </motion.div>
    </div>
  );
};

export const ChatEmptyState = ({
  isLoadingRecommendations,
  keyboardIsOpen,
  starterQuestions,
  isWarmupComplete,
  isWarmingUp,
  warmupError,
  isStreaming,
  onSendRecommendation,
  headerTemplate,
  hasPendingMessage,
}: ChatEmptyStateProps) => {
  const { persona } = useAdonisChat();
  const { user } = useUser();

  const personalizedHeader = user?.firstName
    ? `Hi ${user.firstName}, ${headerTemplate}`
    : headerTemplate;

  const advisorImage =
    persona.name.toLowerCase() === "aria"
      ? ariaAdvisorImage
      : adonisAdvisorImage;

  return (
    <AnimatePresence>
      {isLoadingRecommendations ? (
        <div className="relative flex h-full min-h-0 flex-1 flex-col gap-4 p-4">
          <AdonisChatFixedHeader personalizedHeader={personalizedHeader} />
          <AnimatePresence>
            {advisorImage && !keyboardIsOpen && (
              <AdvisorImage key={advisorImage} src={advisorImage} />
            )}
          </AnimatePresence>
        </div>
      ) : (
        <motion.div
          className="relative flex h-full min-h-0 flex-1 flex-col gap-4 p-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
        >
          <AdonisChatFixedHeader personalizedHeader={personalizedHeader} />
          <AnimatePresence>
            {advisorImage && !keyboardIsOpen && (
              <AdvisorImage key={advisorImage} src={advisorImage} />
            )}
          </AnimatePresence>
          {!keyboardIsOpen && !hasPendingMessage && (
            <div className="absolute inset-x-0 bottom-4 z-10 px-4">
              {warmupError && !isWarmingUp && !isWarmupComplete ? (
                <div className="rounded-lg bg-white px-4 py-6 text-center shadow-md">
                  <p className="font-sans text-base leading-[22px] text-black opacity-80">
                    We are experiencing technical difficulties. Please try again
                    later.
                  </p>
                </div>
              ) : (
                <ChatRecommendations
                  recommendations={
                    starterQuestions ?? defaultAdonisStarterQuestions
                  }
                  onClick={onSendRecommendation}
                  disabled={!isWarmupComplete || isWarmingUp || isStreaming}
                />
              )}
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ChatEmptyState;
